import { useEffect, useRef, useState } from 'react';
import {
  BackgroundJob,
  backgroundPhotoUploads,
  JobFilter,
  matches,
} from '../utils/backgroundPhotoUploads';

/**
 * SW2-2458. Background photo jobs that have not reached the server yet
 * (queued, uploading or failed) matching `filter`. Re-renders whenever a job
 * changes.
 */
export function usePendingPhotoUploads(filter: JobFilter = {}): BackgroundJob[] {
  const { kind, inspectionId, findingId, folderId } = filter;
  const read = () =>
    backgroundPhotoUploads.pending({ kind, inspectionId, findingId, folderId });
  const [pending, setPending] = useState(read);

  useEffect(() => {
    setPending(read());

    return backgroundPhotoUploads.subscribe(() => setPending(read()));
    // read() only depends on the filter values.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, inspectionId, findingId, folderId]);

  return pending;
}

/** Total photos still to upload across the given jobs. */
export const pendingPhotoCount = (jobs: BackgroundJob[]) =>
  jobs.reduce((sum, job) => sum + job.photoCount, 0);

/**
 * Runs `onDone` each time a background job matching `filter` succeeds. Nothing
 * runs while `enabled` is false (e.g. before the page knows its ids).
 */
export function useOnBackgroundJobDone(
  filter: JobFilter,
  onDone: (job: BackgroundJob) => void,
  enabled = true,
) {
  const handler = useRef(onDone);
  const { kind, inspectionId, findingId, folderId } = filter;

  handler.current = onDone;

  useEffect(() => {
    if (!enabled) return undefined;

    return backgroundPhotoUploads.onJobDone((job) => {
      if (matches(job, { kind, inspectionId, findingId, folderId })) {
        handler.current(job);
      }
    });
  }, [enabled, kind, inspectionId, findingId, folderId]);
}

/** Finding photos for this inspection finished uploading. */
export const useOnPhotoUploadDone = (
  inspectionId: number | undefined,
  onDone: (job: BackgroundJob) => void,
) =>
  useOnBackgroundJobDone(
    { kind: 'finding', inspectionId },
    onDone,
    inspectionId !== undefined && inspectionId !== null,
  );
